// src/store/slices/productManagementSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/api/axios';

// API Routes
const API_ROUTES = {
  GET_ALL_PRODUCTS: '/product-management',
  GET_PRODUCT: '/product-management',
  CREATE_PRODUCT: '/product-management',
  UPDATE_PRODUCT: '/product-management',
  DELETE_PRODUCT: '/product-management',
  UPDATE_STOCK: '/product-management',
  TOGGLE_FEATURED: '/product-management',
  TOGGLE_AVAILABILITY: '/product-management',
  IMPORT_CSV: '/product-management/import-csv',
  GET_PRODUCT_STATS: '/product-management/stats',
  DELETE_ALL_PRODUCTS: '/product-management/delete-all'
};

// Thunk Actions
export const getAllProducts = createAsyncThunk(
  'productManagement/getAllProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { 
        page = 1, 
        limit = 10, 
        search = '', 
        category = '',
        subCategory = '',
        material = '',
        color = '',
        isAvailable = '',
        isFeatured = '',
        isEcoFriendly = '',
        sortBy = '',
        sortOrder = '',
        minPrice = '',
        maxPrice = ''
      } = params;
      
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      if (search) queryParams.append('search', search);
      if (category) queryParams.append('category', category);
      if (subCategory) queryParams.append('subCategory', subCategory);
      if (material) queryParams.append('material', material);
      if (color) queryParams.append('color', color);
      if (isAvailable !== '') queryParams.append('isAvailable', isAvailable);
      if (isFeatured !== '') queryParams.append('isFeatured', isFeatured);
      if (isEcoFriendly !== '') queryParams.append('isEcoFriendly', isEcoFriendly);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (minPrice) queryParams.append('minPrice', minPrice);
      if (maxPrice) queryParams.append('maxPrice', maxPrice);
      
      const response = await axiosInstance.get(`${API_ROUTES.GET_ALL_PRODUCTS}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const getProductById = createAsyncThunk(
  'productManagement/getProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_ROUTES.GET_PRODUCT}/${productId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product details');
    }
  }
);

export const createProduct = createAsyncThunk(
  'productManagement/createProduct',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ROUTES.CREATE_PRODUCT, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'productManagement/updateProduct',
  async ({ productId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ROUTES.UPDATE_PRODUCT}/${productId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'productManagement/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`${API_ROUTES.DELETE_PRODUCT}/${productId}`);
      return { ...response.data, productId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete product');
    }
  }
);

export const deleteAllProducts = createAsyncThunk(
  'productManagement/deleteAllProducts',
  async (_, { rejectWithValue }) => {
    try {
      const confirmation = 'CONFIRM_DELETE_ALL_PRODUCTS';
      const response = await axiosInstance.delete(API_ROUTES.DELETE_ALL_PRODUCTS, {
        data: { confirmation }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete all products');
    }
  }
);

export const updateStock = createAsyncThunk(
  'productManagement/updateStock',
  async ({ productId, stock, operation }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ROUTES.UPDATE_STOCK}/${productId}/stock`,
        { stock, operation }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update stock');
    }
  }
);

export const toggleFeatured = createAsyncThunk(
  'productManagement/toggleFeatured',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.TOGGLE_FEATURED}/${productId}/featured`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle featured status');
    }
  }
);

export const toggleAvailability = createAsyncThunk(
  'productManagement/toggleAvailability',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.TOGGLE_AVAILABILITY}/${productId}/availability`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle availability');
    }
  }
);

export const importProductsCSV = createAsyncThunk(
  'productManagement/importProductsCSV',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ROUTES.IMPORT_CSV,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to import products');
    }
  }
);

export const getProductStats = createAsyncThunk(
  'productManagement/getProductStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ROUTES.GET_PRODUCT_STATS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get product statistics');
    }
  }
);

const initialState = {
  products: [],
  currentProduct: null,
  stats: null,
  importResults: null,
  pagination: null,
  loading: false,
  error: null,
  success: false,
  lastUpdateTimestamp: null // Add this to track updates
};

const productManagementSlice = createSlice({
  name: 'productManagement',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    forceRefresh: (state) => {
      // Force component to refetch data
      state.lastUpdateTimestamp = Date.now();
    },
    resetProductState: () => initialState
  },

  extraReducers: (builder) => {
    builder
      // Get All Products
      .addCase(getAllProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.data.products;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch products';
      })

      // Get Product By Id
      .addCase(getProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload.data.product;
      })
      .addCase(getProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch product details';
      })

      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.currentProduct = null; // Clear current product
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create product';
      })

      // Update Product - CRITICAL FIX HERE
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        
        // CRITICAL: Clear current product to force refetch
        state.currentProduct = null;
        
        // CRITICAL: Mark timestamp to trigger refetch
        state.lastUpdateTimestamp = Date.now();
        
        // Optional: Remove updated product from list to force fresh data
        const updatedProductId = action.payload?.data?.productId;
        if (updatedProductId && state.products.length > 0) {
          // Remove from list - this forces getAllProducts to fetch fresh data
          state.products = state.products.filter(p => p.productId !== updatedProductId);
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update product';
      })

      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.products = state.products.filter(
          product => product.productId !== action.meta.arg
        );
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete product';
      })

      // Delete All Products
      .addCase(deleteAllProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAllProducts.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.products = [];
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(deleteAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete all products';
      })

      // Update Stock
      .addCase(updateStock.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateStock.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        
        // Update stock in the products array
        const { productId, currentStock } = action.payload.data;
        
        state.products = state.products.map(product => 
          product.productId === productId ? { ...product, stock: currentStock } : product
        );
        
        // Update current product if it's the same one
        if (state.currentProduct?.productId === productId) {
          state.currentProduct = { ...state.currentProduct, stock: currentStock };
        }
        
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(updateStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update stock';
      })

      // Toggle Featured
      .addCase(toggleFeatured.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(toggleFeatured.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        
        const { productId, isFeatured } = action.payload.data;
        
        // Update in products list
        state.products = state.products.map(product => 
          product.productId === productId ? { ...product, isFeatured } : product
        );
        
        // Update current product if it's the same one
        if (state.currentProduct?.productId === productId) {
          state.currentProduct = { ...state.currentProduct, isFeatured };
        }
        
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(toggleFeatured.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to toggle featured status';
      })

      // Toggle Availability
      .addCase(toggleAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(toggleAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        
        const { productId, isAvailable } = action.payload.data;
        
        // Update in products list
        state.products = state.products.map(product => 
          product.productId === productId ? { ...product, isAvailable } : product
        );
        
        // Update current product if it's the same one
        if (state.currentProduct?.productId === productId) {
          state.currentProduct = { ...state.currentProduct, isAvailable };
        }
        
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(toggleAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to toggle availability';
      })

      // Import Products CSV
      .addCase(importProductsCSV.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.importResults = null;
      })
      .addCase(importProductsCSV.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.importResults = action.payload.data;
        state.lastUpdateTimestamp = Date.now();
      })
      .addCase(importProductsCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to import products';
      })

      // Get Product Stats
      .addCase(getProductStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data.stats;
      })
      .addCase(getProductStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get product statistics';
      });
  }
});

export const { clearError, clearSuccess, resetProductState, clearCurrentProduct, forceRefresh } = productManagementSlice.actions;
export default productManagementSlice.reducer;